import type { Operation, SecurityScheme } from "./types";

type Header = [name: string, value: string];

export const authHeaders = (
  operation: Operation,
  schemes: Record<string, SecurityScheme>,
): Header[] =>
  operation.security.flatMap((key) => {
    const scheme = schemes[key];
    if (!scheme) return [];
    if (scheme.type === "apiKey" && scheme.in === "header" && scheme.name) {
      return [[scheme.name, "<api-key>"] as Header];
    }
    if (scheme.type === "http" && scheme.scheme === "bearer") {
      return [["Authorization", "Bearer <access-token>"] as Header];
    }
    return [];
  });

const json = (value: unknown) => JSON.stringify(value, null, 2);

const indent = (text: string, spaces: number) =>
  text.replace(/\n/g, `\n${" ".repeat(spaces)}`);

export const curlSnippet = (
  operation: Operation,
  url: string,
  headers: Header[],
  body?: unknown,
) => {
  const lines = [`curl ${operation.method === "GET" ? "" : `-X ${operation.method} `}${url}`];
  for (const [name, value] of headers) lines.push(`  -H '${name}: ${value}'`);
  if (body !== undefined) {
    lines.push("  -H 'Content-Type: application/json'");
    lines.push(`  -d '${json(body)}'`);
  }
  return lines.join(" \\\n");
};

export const jsSnippet = (
  operation: Operation,
  url: string,
  headers: Header[],
  body?: unknown,
  resultName = "data",
) => {
  const opts: string[] = [];
  if (operation.method !== "GET") opts.push(`  method: '${operation.method}',`);
  const all = body !== undefined ? [...headers, ["Content-Type", "application/json"] as Header] : headers;
  if (all.length > 0) {
    opts.push("  headers: {");
    for (const [name, value] of all) opts.push(`    '${name}': '${value}',`);
    opts.push("  },");
  }
  if (body !== undefined) opts.push(`  body: JSON.stringify(${indent(json(body), 2)}),`);

  const call =
    opts.length > 0
      ? `await fetch('${url}', {\n${opts.join("\n")}\n})`
      : `await fetch('${url}')`;

  return [`const response = ${call}`, `const ${resultName} = await response.json()`].join("\n");
};
